import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Receipt,
  CreditCard,
  Settings,
  Tag,
  Shield,
  UserCheck,
  ListOrdered,
  Bell,
  BarChart3,
  MessageSquare,
  Plus,
  HelpCircle,
  Wrench,
  Calendar,
  Home,
  ChevronDown,
  ClipboardList,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { NavigationItem } from '../../types';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const navRef = useRef<HTMLDivElement>(null);

  // Check if this is a system admin session
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';

  // Fetch company data to check company type
  const { data: companyResponse } = useQuery({
    queryKey: ['company'],
    queryFn: () => apiService.getCompany(),
    enabled: !isSystemAdmin,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
  
  const companyType = companyResponse?.data?.companyType ?? 0; // Default to Facilities
  const isPropertyManagement = companyType === 9; // PropertyManagement = 9

  // Load expanded state from localStorage
  const loadExpandedState = (): Record<string, boolean> => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = localStorage.getItem('nav-expanded-state');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error loading nav state:', e);
    }
    // Default state: admin sub-groups collapsed, except system-logs for easy access to audit logs
    return {
      'admin': true,
      'users-access': false,
      'work-order-config': false,
      'system-logs': true,
      'settings': true,
    };
  };

  const [expanded, setExpanded] = useState<Record<string, boolean>>(loadExpandedState);

  // Save and restore scroll position only on mount/unmount
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    // Restore scroll position on initial mount
    const savedScrollPos = sessionStorage.getItem('nav-scroll-position');
    if (savedScrollPos) {
      nav.scrollTop = parseInt(savedScrollPos, 10);
    }

    // Save scroll position as user scrolls
    const handleScroll = () => {
      sessionStorage.setItem('nav-scroll-position', nav.scrollTop.toString());
    };

    nav.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup
    return () => {
      nav.removeEventListener('scroll', handleScroll);
    };
  }, []); // Only run once on mount

  // Save expanded state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('nav-expanded-state', JSON.stringify(expanded));
    } catch (e) {
      console.error('Error saving nav state:', e);
    }
  }, [expanded]);

  const toggleSection = (name: string) => {
    setExpanded((prev) => ({ ...prev, [name]: !(prev[name] ?? false) }));
  };

  // Navigation sections structure
  interface NavigationSection {
    title: string;
    items: NavigationItem[];
  }

  const sections: NavigationSection[] = [];

  // System Admin Navigation
  if (isSystemAdmin || user?.roleName === 'SuperAdmin') {
    sections.push({
      title: 'SYSTEM ADMINISTRATION',
      items: [
        {
          name: 'Dashboard',
          href: '/systemadmin',
          icon: LayoutDashboard,
        },
        {
          name: 'Companies',
          href: '/systemadmin',
          icon: Shield,
        },
        {
          name: 'Create Company',
          href: '/systemadmin/companies/create',
          icon: Plus,
        },
        {
          name: 'Subscription Metrics',
          href: '/systemadmin/subscription-metrics',
          icon: BarChart3,
        },
        {
          name: 'Analytics',
          href: '/systemadmin/analytics',
          icon: BarChart3,
        },
        {
          name: 'Audit Logs',
          href: '/audit-logs',
          icon: Shield,
        },
      ],
    });
  } else {
    // Regular User Navigation - Grouped Structure
    
    // PRIMARY OPERATIONS
    const primaryOperations: NavigationItem[] = [
      {
        name: user?.roleName === 'Dispatcher' ? 'Dispatcher Dashboard' : 'Dashboard',
        href: user?.roleName === 'Dispatcher' ? '/dispatcher/dashboard' : '/dashboard',
        icon: user?.roleName === 'Dispatcher' ? ClipboardList : LayoutDashboard,
      },
      ...(user?.roleName === 'Admin'
        ? [{
            name: 'Dispatcher Dashboard',
            href: '/dispatcher/dashboard',
            icon: ClipboardList,
          }]
        : []),
      {
        name: 'Work Orders',
        href: '/workorders',
        icon: FileText,
      },
      {
        name: 'Equipment',
        href: '/equipment',
        icon: Wrench,
      },
      {
        name: 'PM Schedules',
        href: '/maintenance-schedules',
        icon: Calendar,
      },
    ];

    // Add Units for Property Management
    if (isPropertyManagement) {
      primaryOperations.splice(3, 0, {
        name: 'Units',
        href: '/units',
        icon: Home,
      });
    }

    sections.push({
      title: 'PRIMARY OPERATIONS',
      items: primaryOperations,
    });

    // BUSINESS MANAGEMENT
    const businessManagement: NavigationItem[] = [
      {
        name: 'Clients',
        href: '/clients',
        icon: Users,
      },
      {
        name: 'Vendors',
        href: '/vendors',
        icon: Building2,
      },
    ];

    if (hasFeature('hasInvoicing')) {
      businessManagement.push({
        name: 'Invoices',
        href: '/invoices',
        icon: Receipt,
      });
      businessManagement.push({
        name: 'Credit Memos',
        href: '/credit-memos',
        icon: CreditCard,
      });
    }

    sections.push({
      title: 'BUSINESS MANAGEMENT',
      items: businessManagement,
    });

    // INSIGHTS & SUPPORT
    const insightsSupport: NavigationItem[] = [
      {
        name: 'Help',
        href: '/help',
        icon: HelpCircle,
      },
    ];

    if (hasFeature('hasReports')) {
      insightsSupport.unshift({
        name: 'Reports',
        href: '/reports',
        icon: BarChart3,
      });
    }

    sections.push({
      title: 'INSIGHTS & SUPPORT',
      items: insightsSupport,
    });

    // ADMINISTRATION (Admin Only)
    if ((user?.roleName === 'Admin' || user?.roleName === 'SuperAdmin') && !isSystemAdmin) {
      const systemLogsChildren: NavigationItem[] = [
        {
          name: 'System Settings',
          href: '/admin',
          icon: Settings,
        },
        ...(hasFeature('hasVendorCompliance')
          ? [
              {
                name: 'Vendor Compliance',
                href: '/admin/vendor-compliance',
                icon: Shield,
              },
            ]
          : []),
        {
          name: 'Workflow Notifications',
          href: '/admin/workflow-notifications',
          icon: Bell,
        },
        {
          name: 'Announcements',
          href: '/admin/notifications',
          icon: Bell,
        },
        {
          name: 'Audit Logs',
          href: '/audit-logs',
          icon: Shield,
        },
      ];

      const adminSection: NavigationSection = {
        title: 'ADMINISTRATION',
        items: [
          {
            name: 'Admin Dashboard',
            href: '/admin/dashboard',
            icon: BarChart3,
          },
          {
            name: 'Users & Access',
            href: '#',
            icon: UserCheck,
            children: [
              {
                name: 'Users',
                href: '/users',
                icon: Users,
              },
              {
                name: 'Roles',
                href: '/roles',
                icon: UserCheck,
              },
            ],
          },
          {
            name: 'Work Order Config',
            href: '#',
            icon: FileText,
            children: [
              {
                name: 'Categories',
                href: '/categories',
                icon: Tag,
              },
              {
                name: 'Priorities',
                href: '/admin/priorities',
                icon: Tag,
              },
              {
                name: 'Statuses',
                href: '/admin/statuses',
                icon: ListOrdered,
              },
              {
                name: 'Entry Types',
                href: '/admin/entry-types',
                icon: MessageSquare,
              },
              {
                name: 'Templates',
                href: '/workorders/templates',
                icon: FileText,
              },
            ],
          },
          {
            name: 'System & Logs',
            href: '#',
            icon: Shield,
            children: systemLogsChildren,
          },
        ],
      };
      sections.push(adminSection);
    }

    // SETTINGS
    sections.push({
      title: 'SETTINGS',
      items: [
        {
          name: 'Settings',
          href: '/settings',
          icon: Settings,
          children: [
            {
              name: 'Subscription',
              href: '/settings/subscription',
              icon: Settings,
            },
            {
              name: 'Billing',
              href: '/settings/stripe-billing',
              icon: CreditCard,
            },
          ],
        },
      ],
    });
  }

  const NavItem: React.FC<{ item: NavigationItem; isChild?: boolean; isNested?: boolean }> = ({ item, isChild = false, isNested = false }) => {
    const Icon = item.icon;
    
    // Don't render as link if it has children (it's a group header)
    if (item.children && item.href === '#') {
      const sectionKey = item.name.toLowerCase().replace(/\s+/g, '-');
      const isExpanded = expanded[sectionKey] ?? false;
      
      return (
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => toggleSection(sectionKey)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-accent-color-50 dark:hover:bg-accent-color-800 hover:text-accent-color-700 dark:hover:text-accent-color-300 rounded-md transition-colors"
          >
            <span className="flex items-center">
              <Icon className="mr-3 h-4 w-4" />
              {item.name}
          </span>
            <ChevronDown
              className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
          {isExpanded && (
            <div className="space-y-1 ml-4">
              {item.children.map((child) => (
                <NavItem key={child.name} item={child} isNested={true} />
              ))}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <NavLink
        to={item.href}
        end={item.href === '/admin' || item.href === '/settings'}
        className={({ isActive }) =>
          `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
            isActive
              ? 'bg-accent-color-100 dark:bg-accent-color-900 text-accent-color-900 dark:text-white border-r-2 border-accent-color'
              : 'text-gray-700 dark:text-gray-300 hover:bg-accent-color-50 dark:hover:bg-accent-color-800 hover:text-accent-color-700 dark:hover:text-accent-color-300'
          } ${isChild ? 'ml-4' : ''} ${isNested ? 'ml-8' : ''}`
        }
        onClick={onClose}
      >
        <Icon className="mr-3 h-5 w-5" />
        {item.name}
      </NavLink>
    );
  };

  const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <>
      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {title}
      </div>
      <div className="border-b border-gray-200 dark:border-gray-700 mb-2" />
    </>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-900 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 border-b border-gray-200 dark:border-gray-700">
            <img
              src="/WorkOrderHQLOGO.png"
              alt="WorkOrder HQ"
              className="h-10 w-auto"
            />
          </div>

          {/* Navigation */}
          <nav ref={navRef} className="flex-1 px-4 py-6 overflow-y-auto" style={{ scrollBehavior: 'auto' }}>
            {sections.map((section, sectionIndex) => (
              <div key={section.title} className={sectionIndex > 0 ? 'mt-6' : ''}>
                <SectionHeader title={section.title} />
                <div className="space-y-1 mt-1">
                  {section.items.map((item) => {
                    // Handle items with children (collapsible groups)
                    if (item.children && item.href === '#') {
                      return <NavItem key={item.name} item={item} />;
                    }
                    // Handle Settings with children (special case - collapsible)
                    if (item.children && item.href !== '#') {
                      const sectionKey = item.name.toLowerCase().replace(/\s+/g, '-');
                      const isExpanded = expanded[sectionKey] ?? true;
                      
                      return (
                        <div key={item.name} className="space-y-1">
                          <button
                            type="button"
                            onClick={() => toggleSection(sectionKey)}
                            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-accent-color-50 dark:hover:bg-accent-color-800 hover:text-accent-color-700 dark:hover:text-accent-color-300 rounded-md transition-colors"
                          >
                            <span className="flex items-center">
                              <item.icon className="mr-3 h-5 w-5" />
                              {item.name}
                            </span>
                            <ChevronDown
                              className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {isExpanded && (
                            <div className="space-y-1 ml-4">
                              {item.children.map((child) => (
                                <NavItem key={child.name} item={child} isChild />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    // Regular navigation item
                    return <NavItem key={item.name} item={item} />;
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Sidebar footer informational widget */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center justify-between">
              <span>Environment</span>
              <span className="font-medium">{process.env.NODE_ENV === 'development' ? 'Dev' : 'Prod'}</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span>Version</span>
              <span className="font-medium">v1.0</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
